import type { SeatRequestListState } from "../../seatings/seat-requests";
import type { Seating } from "../../seatings/schemas";
import type { LiveState } from "../../seatings/use-seatings";
import type { RequestListState } from "../../track-switch/use-track-switch";

export interface RequestsTab {
  /** Whether the admin page shows the Requests tab. */
  show: boolean;
  /** Open requests of both kinds that could be read. */
  open: number;
  /** Some requests could not be read, so `open` may be short. */
  incomplete: boolean;
}

/**
 * The Requests tab exists while table requests are on (TNG-92), while a
 * seating is published, or while a seat change request is still open
 * (TNG-94), and its badge counts the open ones of both kinds.
 *
 * A read that fails never hides the tab or pretends there is nothing: the
 * tab stays, its badge is marked incomplete ("!" with nothing counted), and
 * the tab itself names what could not be loaded.
 */
export function requestsTab({
  trackSwitchOn,
  requests,
  seatings,
  seatRequests,
}: {
  trackSwitchOn: boolean;
  requests: RequestListState;
  seatings: LiveState<Seating>;
  seatRequests: SeatRequestListState;
}): RequestsTab {
  const seatRequestsIncomplete =
    seatings.status === "error" ||
    seatRequests.status === "error" ||
    (seatRequests.status === "success" && seatRequests.failed.length > 0);
  const incomplete = seatRequestsIncomplete || requests.status === "error";
  const openSeatRequests =
    seatRequests.status === "success"
      ? seatRequests.data.filter((entry) => entry.request.status === "OPEN")
          .length
      : 0;
  const openSwitches =
    requests.status === "success"
      ? requests.data.filter((request) => request.status === "OPEN").length
      : 0;
  const anyPublished =
    seatings.status === "success" &&
    seatings.data.some((seating) => seating.status === "PUBLISHED");
  return {
    show:
      trackSwitchOn ||
      anyPublished ||
      openSeatRequests > 0 ||
      seatRequestsIncomplete,
    open: openSwitches + openSeatRequests,
    incomplete,
  };
}
